/**
 * Admin project collaborators list component for project detail page.
 * Adds an existing workspace member to the project and manages the email report setting.
 */

import { useState } from "react";
import {
  useAdminProjectCollaborators,
  useAdminWorkspaceCollaborators,
  useAdminAddProjectCollaborator,
  useAdminUpdateProjectCollaborator,
  useAdminRemoveProjectCollaborator,
} from "../../hooks/useAdminWorkspaces";
import {
  AlertCircle,
  Loader2,
  Users,
  UserPlus,
  Trash2,
  Mail,
  Phone,
} from "lucide-react";
import type { Collaborator } from "../../services/naraDoor";

interface AdminProjectCollaboratorsListProps {
  workspaceId: string;
  projectId: string;
}

export function AdminProjectCollaboratorsList({ workspaceId, projectId }: AdminProjectCollaboratorsListProps) {
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showRemoveDialog, setShowRemoveDialog] = useState<Collaborator | null>(null);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [addFlags, setAddFlags] = useState({ emailTranscription: false });

  const { data, isLoading, error } = useAdminProjectCollaborators(projectId);
  const { data: membersData } = useAdminWorkspaceCollaborators(workspaceId);
  const addMutation = useAdminAddProjectCollaborator();
  const updateMutation = useAdminUpdateProjectCollaborator();
  const removeMutation = useAdminRemoveProjectCollaborator();

  const collaborators = data?.collaborators || [];
  // Only existing workspace members can be added; someone new joins through an invite.
  const projectUserIds = new Set(collaborators.map((c) => c.user_id));
  const availableMembers = (membersData?.collaborators || []).filter((m) => !projectUserIds.has(m.user_id));

  const closeAddDialog = () => {
    setShowAddDialog(false);
    setSelectedUserId("");
    setAddFlags({ emailTranscription: false });
    addMutation.reset();
  };

  const handleAddCollaborator = async () => {
    try {
      await addMutation.mutateAsync({ projectId, userId: selectedUserId, ...addFlags });
      closeAddDialog();
    } catch (err) {
      console.error("Failed to add project collaborator:", err instanceof Error ? err.message : err);
    }
  };

  const handleToggleEmailTranscription = async (collaborator: Collaborator) => {
    try {
      await updateMutation.mutateAsync({
        projectId,
        userId: collaborator.user_id,
        emailTranscription: !collaborator.email_transcription,
      });
    } catch (err) {
      console.error("Failed to update email transcription:", err);
    }
  };

  const handleRemoveCollaborator = async () => {
    if (!showRemoveDialog) return;

    try {
      await removeMutation.mutateAsync({
        projectId,
        userId: showRemoveDialog.user_id,
      });
      setShowRemoveDialog(null);
    } catch (err) {
      console.error("Failed to remove collaborator:", err);
    }
  };

  const getCollaboratorName = (collaborator: Collaborator) => {
    if (collaborator.user?.first_name || collaborator.user?.last_name) {
      return `${collaborator.user.first_name || ""} ${collaborator.user.last_name || ""}`.trim();
    }
    return collaborator.user?.email || "Unknown";
  };

  const getContactInfo = (collaborator: Collaborator) => {
    const parts = [];
    if (collaborator.user?.email) parts.push(collaborator.user.email);
    if (collaborator.user?.phone) parts.push(collaborator.user.phone);
    return parts.join(" / ") || "—";
  };

  if (isLoading) {
    return (
      <div className="space-y-2">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-14 bg-muted rounded-lg animate-pulse" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-3 p-4 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg">
        <AlertCircle className="h-5 w-5 text-red-600 shrink-0" />
        <p className="text-sm text-red-800 dark:text-red-200">{error.message}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          {collaborators.length} collaborator{collaborators.length !== 1 ? "s" : ""}
        </p>
        <button
          onClick={() => setShowAddDialog(true)}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <UserPlus className="h-4 w-4" />
          Add Collaborator
        </button>
      </div>

      {collaborators.length === 0 ? (
        <div className="text-center py-8 bg-muted rounded-lg border border-border">
          <Users className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">No collaborators in this project</p>
        </div>
      ) : (
        <div className="bg-card rounded-lg border border-border overflow-hidden">
          {/* Mobile: Card list */}
          <div className="sm:hidden divide-y divide-border">
            {collaborators.map((collaborator) => (
              <div key={collaborator.id} className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium text-foreground truncate">
                        {getCollaboratorName(collaborator)}
                      </span>
                    </div>
                    <div className="flex flex-col gap-0.5 mb-3">
                      <span className="flex items-center gap-1 text-sm text-muted-foreground">
                        <Mail className="h-3 w-3 text-muted-foreground" />
                        {collaborator.user?.email || "—"}
                      </span>
                      <span className="flex items-center gap-1 text-sm text-muted-foreground">
                        <Phone className="h-3 w-3 text-muted-foreground" />
                        {collaborator.user?.phone || "—"}
                      </span>
                    </div>
                    <div className="flex items-center gap-4">
                      <label className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={collaborator.email_transcription || false}
                          onChange={() => handleToggleEmailTranscription(collaborator)}
                          disabled={updateMutation.isPending}
                          className="h-4 w-4 text-blue-600 rounded border-border focus:ring-blue-500"
                        />
                        Email Report
                      </label>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowRemoveDialog(collaborator)}
                    className="p-2 text-muted-foreground hover:text-red-600 rounded transition-colors"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Desktop: Table */}
          <table className="w-full hidden sm:table">
            <thead className="bg-muted border-b border-border">
              <tr>
                <th className="text-left px-4 py-3 text-sm font-medium text-foreground">
                  Name
                </th>
                <th className="text-left px-4 py-3 text-sm font-medium text-foreground">
                  Contact
                </th>
                <th className="text-center px-4 py-3 text-sm font-medium text-foreground">
                  Email Report
                </th>
                <th className="text-center px-4 py-3 text-sm font-medium text-foreground">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {collaborators.map((collaborator) => (
                <tr key={collaborator.id} className="hover:bg-accent">
                  <td className="px-4 py-3">
                    <span className="font-medium text-foreground">
                      {getCollaboratorName(collaborator)}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-col">
                      <span className="flex items-center gap-1 text-sm text-foreground">
                        <Mail className="h-4 w-4 text-muted-foreground" />
                        {collaborator.user?.email || "—"}
                      </span>
                      <span className="flex items-center gap-1 text-sm text-muted-foreground mt-0.5">
                        <Phone className="h-4 w-4 text-muted-foreground" />
                        {collaborator.user?.phone || "—"}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={collaborator.email_transcription || false}
                        onChange={() => handleToggleEmailTranscription(collaborator)}
                        disabled={updateMutation.isPending}
                        className="sr-only peer"
                      />
                      <div className="w-9 h-5 bg-gray-200 dark:bg-gray-700 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-border dark:after:border-gray-600 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <button
                      onClick={() => setShowRemoveDialog(collaborator)}
                      className="p-2 text-muted-foreground hover:text-red-600 rounded transition-colors"
                      title="Remove collaborator"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add Collaborator Dialog */}
      {showAddDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-card rounded-lg shadow-xl w-full max-w-sm mx-4">
            <div className="p-4 space-y-4">
              <div>
                <h2 className="text-lg font-semibold text-foreground mb-1">Add Collaborator</h2>
                <p className="text-sm text-muted-foreground">
                  Add a workspace member to this project. Invite someone new from the workspace first.
                </p>
              </div>
              {addMutation.error && (
                <div className="p-3 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-600">
                  {addMutation.error.message}
                </div>
              )}
              <select
                value={selectedUserId}
                onChange={(e) => setSelectedUserId(e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-lg bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">
                  {availableMembers.length ? "Select a member" : "No workspace members to add"}
                </option>
                {availableMembers.map((m) => (
                  <option key={m.user_id} value={m.user_id}>
                    {[m.user?.first_name, m.user?.last_name].filter(Boolean).join(" ") || m.user?.email || m.user_id}
                  </option>
                ))}
              </select>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={addFlags.emailTranscription}
                    onChange={(e) => setAddFlags({ ...addFlags, emailTranscription: e.target.checked })}
                    className="h-4 w-4 text-blue-600 rounded border-border focus:ring-blue-500"
                  />
                  Email Report
                </label>
              </div>
              <div className="flex justify-end gap-3">
                <button
                  onClick={closeAddDialog}
                  disabled={addMutation.isPending}
                  className="px-4 py-2 text-foreground hover:bg-accent rounded-lg transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddCollaborator}
                  disabled={!selectedUserId || addMutation.isPending}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  {addMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                  Add Collaborator
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Remove Collaborator Confirmation Dialog */}
      {showRemoveDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-card rounded-lg shadow-xl w-full max-w-sm mx-4">
            <div className="p-4">
              <h2 className="text-lg font-semibold text-foreground mb-2">
                Remove Collaborator
              </h2>
              <p className="text-sm text-muted-foreground mb-4">
                Are you sure you want to remove{" "}
                <span className="font-medium">{getCollaboratorName(showRemoveDialog)}</span>{" "}
                from this project?
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowRemoveDialog(null)}
                  disabled={removeMutation.isPending}
                  className="px-4 py-2 text-foreground hover:bg-accent rounded-lg transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleRemoveCollaborator}
                  disabled={removeMutation.isPending}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                >
                  {removeMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                  Remove
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
